import { useState } from 'react';
import { TFunction, useTranslation } from 'react-i18next';

import ErrorIcon from '@mui/icons-material/Error';
import Box from '@mui/material/Box';
import { GridColDef, GridRowParams, GridSlotProps } from '@mui/x-data-grid-premium';

import DataGrid from '../../../components/DataGrid/DataGrid';
import { TipShareShift, TipShareShiftRow } from '../../types';
import SchedulingUtilities from '../../utilities/schedulingUtilities';
import {
  getShiftsGridIncompleteCoverageSx,
  shiftsGridTimeCellContentSx,
  shiftsGridWarningHeaderContentSx,
  shiftsGridWarningHeaderIconSx,
} from '../styles';
import ShiftActionsMenu from './ShiftActionsMenu';

interface ShiftsDataGridProps {
  loading: boolean;
  data: TipShareShift[] | undefined;
  isEditor: boolean;
  highlightIncompleteCoverageEndTime?: boolean;
  onRowClick: (shift: TipShareShift) => void;
  onEditClick: (shift: TipShareShift) => void;
  onDeleteClick: (shift: TipShareShift) => void;
}

const gridSlotProps: Partial<GridSlotProps> = {
  loadingOverlay: {
    variant: 'linear-progress',
    noRowsVariant: 'skeleton',
  },
};

const mapShiftRows = (shiftsData: TipShareShift[] | void | undefined): TipShareShiftRow[] => {
  if (!shiftsData || typeof shiftsData === 'undefined') return [];
  return shiftsData.map((shift) => ({
    id: shift.id,
    name: shift.name,
    startTime: shift.startTime,
    endTime: shift.endTime,
    shift,
  }));
};

const formatTimeDisplay = (time: string | undefined): string => SchedulingUtilities.formatTime24To12(time) || '-';

const endTimeWarningHeaderClassName = 'end-time-warning-header';
const endTimeWarningCellClassName = 'end-time-warning-cell';

const getTimeColumn = (
  t: TFunction<'translation', undefined>,
  fieldName: 'startTime' | 'endTime',
  highlightIncompleteCoverageEndTime = false
): GridColDef => ({
  field: fieldName,
  headerName: t(`labor.tipShareShifts.columns.${fieldName}`),
  flex: 2,
  minWidth: 120,
  align: 'left',
  headerAlign: 'left',
  headerClassName:
    fieldName === 'endTime' && highlightIncompleteCoverageEndTime ? endTimeWarningHeaderClassName : undefined,
  renderHeader: () => {
    const shouldHighlight = fieldName === 'endTime' && highlightIncompleteCoverageEndTime;

    if (!shouldHighlight) {
      return t(`labor.tipShareShifts.columns.${fieldName}`);
    }

    return (
      <Box sx={shiftsGridWarningHeaderContentSx} data-testid='end-time-header-warning-content'>
        {t(`labor.tipShareShifts.columns.${fieldName}`)}
        <ErrorIcon data-testid='end-time-header-warning-icon' color='error' sx={shiftsGridWarningHeaderIconSx} />
      </Box>
    );
  },
  cellClassName:
    fieldName === 'endTime' && highlightIncompleteCoverageEndTime ? endTimeWarningCellClassName : undefined,
  valueGetter: (_value: string, row: TipShareShiftRow) => row?.[fieldName] ?? '-',
  renderCell: ({ value }) => {
    const shouldHighlight = fieldName === 'endTime' && highlightIncompleteCoverageEndTime;

    return (
      <Box data-testid={shouldHighlight ? 'end-time-cell-warning' : undefined} sx={shiftsGridTimeCellContentSx}>
        {formatTimeDisplay(value)}
      </Box>
    );
  },
});

function ShiftsDataGrid({
  loading,
  data,
  isEditor,
  highlightIncompleteCoverageEndTime = false,
  onRowClick,
  onEditClick,
  onDeleteClick,
}: Readonly<ShiftsDataGridProps>) {
  const { t } = useTranslation();

  // Pagination state
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // Menu state for actions
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuRowId, setMenuRowId] = useState<string | null>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, rowId: string) => {
    setAnchorEl(event.currentTarget);
    setMenuRowId(rowId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuRowId(null);
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: t('labor.tipShareShifts.columns.name'),
      flex: 3,
      minWidth: 150,
      align: 'left',
      headerAlign: 'left',
      valueGetter: (value: string, row: TipShareShiftRow) => row?.name ?? '',
    },
    getTimeColumn(t, 'startTime'),
    getTimeColumn(t, 'endTime', highlightIncompleteCoverageEndTime),
    {
      field: 'actions',
      headerName: t('labor.tipShareShifts.columns.actions'),
      flex: 1,
      minWidth: 80,
      align: 'left',
      headerAlign: 'left',
      sortable: false,
      filterable: false,
      renderCell: (params: GridRowParams<TipShareShiftRow>) => {
        if (!isEditor) return null;
        return (
          <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-start' }}>
            <ShiftActionsMenu
              shift={params.row.shift}
              shiftId={params.row.id}
              anchorEl={menuRowId === params.row.id ? anchorEl : null}
              isOpen={menuRowId === params.row.id && Boolean(anchorEl)}
              onOpen={(e) => handleMenuOpen(e, params.row.id)}
              onClose={handleMenuClose}
              onEdit={onEditClick}
              onDelete={onDeleteClick}
              t={t}
            />
          </Box>
        );
      },
    } as unknown as GridColDef,
  ];

  const gridSx = highlightIncompleteCoverageEndTime
    ? getShiftsGridIncompleteCoverageSx(endTimeWarningHeaderClassName, endTimeWarningCellClassName)
    : undefined;

  return (
    <DataGrid
      loading={loading}
      columns={columns}
      sx={gridSx}
      slotProps={gridSlotProps}
      rows={loading ? [] : mapShiftRows(data)}
      pagination
      page={page}
      paginationModel={{ page, pageSize }}
      onPageChange={setPage}
      onPageSizeChange={setPageSize}
      itemsPerPage={pageSize}
      onRowClick={(params) => onRowClick(params.row.shift)}
      data-testid='shifts-grid'
    />
  );
}

export default ShiftsDataGrid;
